import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { adminService, type KPAFramework, type KPAFrameworkStats, type KPAFrameworkItem } from '@/services/api/adminService';
import toast from 'react-hot-toast';
import KPAFormModal from './KPAFormModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const SystemConfiguration: React.FC = () => {
  // KPA Framework state
  const [kpaFramework, setKpaFramework] = useState<KPAFramework | null>(null);
  const [kpaStats, setKpaStats] = useState<KPAFrameworkStats | null>(null);
  const [loadingKPA, setLoadingKPA] = useState(false);
  const [kpaModalOpen, setKpaModalOpen] = useState(false);
  const [kpaModalMode, setKpaModalMode] = useState<'create' | 'edit'>('create');
  const [selectedKPA, setSelectedKPA] = useState<KPAFrameworkItem | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [kpaToDelete, setKpaToDelete] = useState<KPAFrameworkItem | null>(null);

  // Fetch KPA Framework data on mount
  useEffect(() => {
    fetchKPAFramework();
  }, []);

  const fetchKPAFramework = async () => {
    try {
      setLoadingKPA(true);
      const [framework, stats] = await Promise.all([
        adminService.getKPAFramework(),
        adminService.getKPAFrameworkStats()
      ]);
      setKpaFramework(framework);
      setKpaStats(stats);
    } catch (error) {
      console.error('Error fetching KPA framework:', error);
      toast.error('Failed to load KPA Framework');
    } finally {
      setLoadingKPA(false);
    }
  };

  const handleCreateKPA = () => {
    setKpaModalMode('create');
    setSelectedKPA(null);
    setKpaModalOpen(true);
  };

  const handleEditKPA = (kpa: KPAFrameworkItem) => {
    setKpaModalMode('edit');
    setSelectedKPA(kpa);
    setKpaModalOpen(true);
  };

  const handleDeleteKPA = (kpa: KPAFrameworkItem) => {
    setKpaToDelete(kpa);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteKPA = async () => {
    if (!kpaToDelete) return;

    try {
      await adminService.deleteKPA(kpaToDelete.id);
      toast.success('KPA deleted successfully');
      fetchKPAFramework(); // Refresh the list
    } catch (error: any) {
      console.error('Error deleting KPA:', error);

      // Check if it's a validation error (400 status)
      if (error.response?.status === 400) {
        // Show the detailed error message from the server
        const errorMessage = error.response?.data?.detail || 'Cannot delete KPA with linked data';
        toast.error(errorMessage, { duration: 6000 });
      } else {
        toast.error('Failed to delete KPA');
      }
    }
  };

  const handleKPAFormSuccess = () => {
    fetchKPAFramework(); // Refresh the list after create/update
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">KPA Framework</h2>
          <p className="text-gray-600 mt-1">
            Configure Key Impact & growth Areas aligned to strategic pillars
          </p>
        </div>
      </div>

      {/* KPA Framework Content */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">KPA Framework Setup</h3>
            <p className="text-sm text-gray-600 mt-1">
              Manage KPAs, weights, and pillar alignment
            </p>
          </div>
          <Button
            className="bg-heritage-blue hover:bg-heritage-blue/90"
            onClick={handleCreateKPA}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add KPA
          </Button>
        </div>

        {/* KPA Stats */}
        {kpaStats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="p-3 bg-heritage-blue/10 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Total KPAs</p>
              <p className="text-2xl font-bold text-heritage-blue">{kpaStats.total_kpas}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Total KPIs</p>
              <p className="text-2xl font-bold text-emerald-600">{kpaStats.linked_kpis}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Total Objectives</p>
              <p className="text-2xl font-bold text-amber-600">{kpaStats.linked_objectives}</p>
            </div>
            <div className="p-3 bg-future-blue/10 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Avg KPIs per KPA</p>
              <p className="text-2xl font-bold text-future-blue">{kpaStats.avg_kpis_per_kpa}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Avg Objectives per KPA</p>
              <p className="text-2xl font-bold text-gray-900">{kpaStats.avg_objectives_per_kpa}</p>
            </div>
          </div>
        )}

        {loadingKPA ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-heritage-blue border-t-transparent"></div>
            <p className="text-gray-600 mt-4">Loading KPA Framework...</p>
          </div>
        ) : kpaFramework && kpaFramework.framework.length > 0 ? (
          <div className="space-y-3">
            {/* Get unique KPAs by extracting and deduplicating from all pillars */}
            {(() => {
              const uniqueKPAsMap = new Map();
              kpaFramework.framework.forEach(pillar => {
                pillar.kpas.forEach(kpa => {
                  if (!uniqueKPAsMap.has(kpa.id)) {
                    uniqueKPAsMap.set(kpa.id, kpa);
                  }
                });
              });
              const uniqueKPAs = Array.from(uniqueKPAsMap.values()).sort((a, b) => a.sort_order - b.sort_order);

              return uniqueKPAs.map((kpa) => (
                <div key={kpa.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{kpa.display_name}</h4>
                      <p className="text-sm text-gray-600">{kpa.description || 'No description'}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditKPA(kpa)}
                        title="Edit KPA"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteKPA(kpa)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete KPA"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-6 text-sm">
                    <div>
                      <span className="text-gray-600">Weight: </span>
                      <span className="font-semibold text-gray-900">{kpa.weight_percentage || 'N/A'}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Sort Order: </span>
                      <span className="font-semibold text-gray-900">{kpa.sort_order}</span>
                    </div>
                    {kpa.created_at && (
                      <div>
                        <span className="text-gray-600">Created: </span>
                        <span className="font-semibold text-gray-900">
                          {new Date(kpa.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-600">
            <p>No KPA framework data available</p>
          </div>
        )}
      </Card>

      {/* KPA Form Modal */}
      <KPAFormModal
        isOpen={kpaModalOpen}
        onClose={() => setKpaModalOpen(false)}
        onSuccess={handleKPAFormSuccess}
        kpa={selectedKPA}
        mode={kpaModalMode}
        strategicPillars={kpaFramework?.framework.map(p => ({
          id: p.pillar_id,
          name: p.pillar_name
        })) || []}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDeleteKPA}
        title="Delete KPA"
        message={`Are you sure you want to delete "${kpaToDelete?.display_name}"?\n\nThis will soft-delete the KPA and remove it from all displays.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default SystemConfiguration;
